/**
 * 荷電粒子砲発動時の視覚エフェクトの状態更新および描画。LANCER専用機能
 *
 * チャージ中のエフェクト、発射時の巨大ビーム、画面の揺れなどを管理します。
 *
 * @module effects/cannon-effects
 */

import { getP5 } from '../../core/p5-instance';
import { CONFIG } from '../../config/game-config';
import {
    getCannonCharging,
    getCannonActive,
    getCannonTimer,
    getPlayer,
    getPowerLevel,
    getScreenShake,
    setScreenShake
} from '../../store';

/**
 * 荷電粒子砲エフェクトに付随する動的な状態（画面揺れなど）を更新します。
 */
export function updateCannonEffect(): void {
    const p = getP5();
    const cannonActive = getCannonActive();

    if (cannonActive) {
        // 発射中は継続的に画面を揺らす
        if (p.frameCount % 3 === 0) {
            const currentShake = getScreenShake();
            // 一定以上の揺れを維持
            setScreenShake(p.max(currentShake, 2));
        }
    }
}

/**
 * 荷電粒子砲のエフェクト（チャージ・発射）を画面に描画します。
 * 注意: この関数は現在の状態に基づいて描画のみを行い、数値の更新は行いません。
 */
export function renderCannonEffect(): void {
    const p = getP5();
    const player = getPlayer();
    if (!player) {
        return;
    }

    const powerLevel = getPowerLevel();
    const cannonCharging = getCannonCharging();
    const cannonActive = getCannonActive();
    const cannonTimer = getCannonTimer();

    const lancerCfg = CONFIG.ships.lancer;
    const cannonCfg = lancerCfg.cannonByPower;
    const lvIndex = p.constrain(powerLevel - 1, 0, 3);
    const colorIntensity = cannonCfg.colorIntensity[lvIndex];
    const baseBeamWidth = cannonCfg.beamWidth[lvIndex];
    const duration = cannonCfg.duration[lvIndex];

    // --- チャージ中の演出 ---
    if (cannonCharging) {
        const chargeProgress = 1 - (cannonTimer / lancerCfg.cannonChargeDuration);

        // 1. チャージリングが回転しながら収束する円形
        const ringCount = p.floor(chargeProgress * (2 + powerLevel)) + 1;
        for (let r = 0; r < ringCount; r++) {
            const ringSize = (25 + powerLevel * 3) + r * 15 + p.sin(p.frameCount * 0.3 + r) * 5;
            const ringAlpha = (150 - r * 30) * chargeProgress * colorIntensity;
            p.noFill();
            p.stroke(200, 50, 255, ringAlpha);
            p.strokeWeight(1.5 + powerLevel * 0.3);
            p.circle(player.x, player.y - 20, ringSize * chargeProgress);
        }

        // 2. 収束パーティクル：周囲から中心に集まる
        const particleCount = 2 + powerLevel;
        for (let i = 0; i < particleCount; i++) {
            const angle = p.random(p.TWO_PI);
            const dist = p.random(50, 100) * (1 - chargeProgress);
            p.fill(200, 100, 255, 150 * colorIntensity);
            p.noStroke();
            p.circle(player.x + p.cos(angle) * dist, player.y - 20 + p.sin(angle) * dist, p.random(2, 4 + powerLevel));
        }

        // === 砲口エフェクト ===
        // 砲口位置（ship-renderer.tsと同じ基準）
        const shipSize = player.size;
        const leftMuzzleX = player.x - shipSize / 4;
        const rightMuzzleX = player.x + shipSize / 4;
        const muzzleY = player.y - shipSize / 2 - 5;

        // 砲口収束パーティクル（前方から多く集まる）
        const muzzleParticleCount = 5 + powerLevel * 2;
        for (let i = 0; i < muzzleParticleCount; i++) {
            // 前方（上方向 = -PI/2）を中心に±100度の範囲からパーティクルが集まる
            const frontAngle = -p.HALF_PI;
            const spread = p.random(-p.PI * 0.55, p.PI * 0.55);
            const muzzleAngle = frontAngle + spread;
            const muzzleDist = p.random(25, 70) * (1 - chargeProgress);
            const muzzleParticleSize = p.random(3, 6 + powerLevel);

            p.fill(255, 150, 255, 230 * colorIntensity);
            p.noStroke();
            // 左砲口への収束
            p.circle(leftMuzzleX + p.cos(muzzleAngle) * muzzleDist, muzzleY + p.sin(muzzleAngle) * muzzleDist, muzzleParticleSize);
            // 右砲口への収束
            p.circle(rightMuzzleX + p.cos(muzzleAngle) * muzzleDist, muzzleY + p.sin(muzzleAngle) * muzzleDist, muzzleParticleSize);
        }

        // 砲口プラズマ球（チャージ進行に応じて成長、サイズと明るさを強化）
        const plasmaSize = (8 + powerLevel * 3) * chargeProgress;
        if (plasmaSize > 1) {
            p.noStroke();

            // 最外側グロー（大きく広がる）
            p.fill(180, 50, 255, 80 * colorIntensity);
            p.circle(leftMuzzleX, muzzleY, plasmaSize * 3);
            p.circle(rightMuzzleX, muzzleY, plasmaSize * 3);

            // 外側グロー
            p.fill(220, 80, 255, 120 * colorIntensity);
            p.circle(leftMuzzleX, muzzleY, plasmaSize * 2.2);
            p.circle(rightMuzzleX, muzzleY, plasmaSize * 2.2);

            // 中間グロー
            p.fill(240, 150, 255, 180 * colorIntensity);
            p.circle(leftMuzzleX, muzzleY, plasmaSize * 1.5);
            p.circle(rightMuzzleX, muzzleY, plasmaSize * 1.5);

            // 内側グロー
            p.fill(255, 200, 255, 220 * colorIntensity);
            p.circle(leftMuzzleX, muzzleY, plasmaSize);
            p.circle(rightMuzzleX, muzzleY, plasmaSize);

            // 白熱コア
            p.fill(255, 255, 255, 255 * colorIntensity);
            p.circle(leftMuzzleX, muzzleY, plasmaSize * 0.5);
            p.circle(rightMuzzleX, muzzleY, plasmaSize * 0.5);
        }

        // 3. テキスト表示
        p.fill(200, 100, 255, 200 * colorIntensity);
        p.textSize(12);
        p.textAlign(p.CENTER, p.CENTER);
        p.text('CHARGING', player.x, player.y - 60);
    }

    // --- 発射時の演出 ---
    if (cannonActive) {
        // ビーム幅の計算（わずかに脈動させる）
        const beamWidth = baseBeamWidth + p.sin(p.frameCount * 0.5) * (8 + powerLevel * 2);

        // ビーム形状の定義
        const beamTopY = 0;                    // ビーム上端
        const beamBottomY = player.y - 30;    // ビーム下端（砲口付近）
        const expandY = beamBottomY - 50;      // ここで最大幅に達する
        const muzzleWidthRatio = 0.25;         // 砲口での幅（最大幅の25%）

        // ビーム描画用ヘルパー関数（砲口付近で細く、すぐに最大幅に広がる）
        const drawTaperedBeam = (widthMult: number, r: number, g: number, b: number, a: number): void => {
            const w = beamWidth * widthMult;
            const muzzleW = w * muzzleWidthRatio;
            p.fill(r, g, b, a * colorIntensity);
            p.beginShape();
            // 上部（最大幅）
            p.vertex(player.x - w, beamTopY);
            p.vertex(player.x + w, beamTopY);
            // 広がり終点（最大幅）
            p.vertex(player.x + w, expandY);
            // 砲口付近（細い）
            p.vertex(player.x + muzzleW, beamBottomY);
            p.vertex(player.x - muzzleW, beamBottomY);
            // 広がり終点（最大幅）
            p.vertex(player.x - w, expandY);
            p.endShape(p.CLOSE);
        };

        p.noStroke();

        // ビームの多層描画：中心ほど明るく白くする
        drawTaperedBeam(1.0, 200, 50, 255, 80);      // 外側グロー
        drawTaperedBeam(0.8, 220, 80, 255, 100);     // 外側グロー2層目
        drawTaperedBeam(0.5, 230, 120, 255, 150);    // 中間グロー
        drawTaperedBeam(0.3, 255, 180, 255, 200);    // コアビーム
        drawTaperedBeam(0.15, 255, 255, 255, 230);   // 中心の白い線

        // === 砲口エフェクト（発射時） ===
        const muzzleX = player.x;
        const muzzleY = beamBottomY;

        // 1. マズルフラッシュ（脈動するグロー）
        const flashPulse = p.sin(p.frameCount * 0.8) * 0.3 + 1;
        const flashSize = (20 + powerLevel * 5) * flashPulse;

        // 外側グロー
        p.fill(200, 50, 255, 60 * colorIntensity);
        p.circle(muzzleX, muzzleY, flashSize * 2);

        // 中間グロー
        p.fill(230, 120, 255, 100 * colorIntensity);
        p.circle(muzzleX, muzzleY, flashSize * 1.4);

        // コアフラッシュ
        p.fill(255, 200, 255, 180 * colorIntensity);
        p.circle(muzzleX, muzzleY, flashSize);

        // 白熱中心
        p.fill(255, 255, 255, 220 * colorIntensity);
        p.circle(muzzleX, muzzleY, flashSize * 0.5);

        // 2. 放射パーティクル（斜め下方向に飛び散る）
        const particleCount = 2 + powerLevel;
        for (let i = 0; i < particleCount; i++) {
            // 左右斜め下方向（±30〜60度）
            const side = p.random() < 0.5 ? -1 : 1;
            const angle = p.HALF_PI + side * p.random(0.5, 1.0); // 下向き±30〜60度
            const dist = p.random(10, 40);
            const particleSize = p.random(2, 5 + powerLevel);

            p.fill(255, 180, 255, p.random(150, 220) * colorIntensity);
            p.circle(
                muzzleX + p.cos(angle) * dist,
                muzzleY + p.sin(angle) * dist,
                particleSize
            );
        }

        // ビーム内のスパーク：不規則な光の点
        const sparkCount = 6 + powerLevel * 2;
        for (let i = 0; i < sparkCount; i++) {
            const sparkX = player.x + p.random(-beamWidth * 0.7, beamWidth * 0.7);
            const sparkY = p.random(0, player.y - 30);
            p.fill(255, 255, 255, p.random(180, 255) * colorIntensity);
            p.circle(sparkX, sparkY, p.random(3, 8 + powerLevel));
        }

        // 電撃エフェクト（ジグザグな線）
        const boltCount = 1 + p.floor(powerLevel / 2);
        p.stroke(255, 200, 255, 150 * colorIntensity);
        p.strokeWeight(1.5 + powerLevel * 0.3);
        for (let i = 0; i < boltCount; i++) {
            const startY = p.random(0, player.y - 50);
            let boltX = player.x + p.random(-beamWidth * 0.4, beamWidth * 0.4);
            p.beginShape();
            p.vertex(boltX, startY);
            for (let j = 0; j < 4; j++) {
                boltX += p.random(-15, 15);
                p.vertex(boltX, startY + (j + 1) * 30);
            }
            p.endShape();
        }
        p.noStroke();

        // 発射開始時の画面フラッシュ
        if (cannonTimer > duration - 15) {
            const flashAlpha = (cannonTimer - (duration - 15)) / 15 * 150 * colorIntensity;
            p.fill(200, 100, 255, flashAlpha);
            p.rect(0, 0, p.width, p.height);
        }
    }
}
